import { useMemo, useState } from "react";
import { ComboBoxItem } from "@/utils/helper.util";
import { NaceIndustryDto } from "@advanced-sales-technologies/backend-client/models/NaceIndustryDto";
import { Controller, RegisterOptions, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowSmallRight } from "react-icons/hi2";

import { useGetAllNaceIndustries } from "@/hooks/api/search/useGetAllNaceIndustries";
import Button from "@/components/atoms/Button";
import NumericBadge from "@/components/atoms/NumericBadge";
import FormControl, { FormControlProps } from "@/components/molecules/form/FormControl";
import Modal from "@/components/molecules/Modal";
import SelectedOptions from "@/components/molecules/SelectedOptions";
import IndustryAllModal from "@/components/organisms/filter/industry/IndustryAllModal";

type FormNACEIndustriesSelectProps = {
  name: string;
  rules?: RegisterOptions;
} & Omit<FormControlProps, "id" | "children">;

type PartialIndustryRef = {
  id: string;
  name?: string;
};

const FormNACEIndustriesSelect = ({ name, rules, ...rest }: FormNACEIndustriesSelectProps) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const values: ComboBoxItem[] = useWatch({ name: name }) || [];

  const { control, setValue } = useFormContext();

  const handleRemoveSelectedItem = (values: ComboBoxItem[], item: ComboBoxItem) => {
    const updatedValue = values.filter((v) => v.id !== item.id);
    setValue(name, updatedValue);
  };

  const { data: industries = [] } = useGetAllNaceIndustries({});

  const buildIndustryIndex = (
    catalogue: NaceIndustryDto[],
    map: Map<string, string> = new Map(),
  ): Map<string, string> => {
    for (const node of catalogue) {
      map.set(node.id, node.title);
      if (node.children?.length) {
        buildIndustryIndex(node.children, map);
      }
    }
    return map;
  };

  const getIndustriesById = (refs: PartialIndustryRef[], index: Map<string, string>) => {
    return refs.map(({ id }) => ({
      id,
      name: index.get(id) ?? "",
    }));
  };

  const industryIndex = useMemo(() => buildIndustryIndex(industries), [industries]);

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => {
        return (
          <FormControl id={name} error={fieldState.error?.message} {...rest}>
            <>
              <div className={"flex items-center justify-between rounded-lg border border-secondary px-3 py-0.5"}>
                <Button
                  onClick={() => setIsOpen(true)}
                  trailingIcon={<HiOutlineArrowSmallRight className={"size-4 stroke-2"} />}
                  label={t("search.filter.industry.advanced.seeAll")}
                  size={"xs"}
                  variants={"text"}
                  color={"natural"}
                  className={"mt-0.5 !pl-0"}
                />

                <NumericBadge value={values.length} color={"blue"} />
              </div>

              {field.value && (
                <SelectedOptions
                  value={getIndustriesById(field.value, industryIndex)}
                  displayValue={(f) => f.name}
                  chipClassName={"max-w-1/2"}
                  handleRemoveSelectedItem={(item) => handleRemoveSelectedItem(field.value, item)}
                  maxDisplayedChips={10}
                  onSeeAllValues={() => setIsOpen(true)}
                />
              )}

              <Modal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                size={"lg"}
                content={IndustryAllModal}
                contentProps={{
                  formName: name,
                  onSuccess: () => setIsOpen(false),
                }}
              />
            </>
          </FormControl>
        );
      }}
    />
  );
};

export default FormNACEIndustriesSelect;
